import { analytics } from "./analytics";

// Enhanced user journey tracking
export interface UserJourney {
  sessionId: string;
  startTime: Date;
  currentStep: string;
  steps: JourneyStep[];
  deviceInfo: DeviceInfo;
  userAgent: string;
  referrer: string;
}

export interface JourneyStep {
  step: string;
  timestamp: Date;
  duration?: number;
  properties?: Record<string, any>;
}

export interface DeviceInfo {
  type: "mobile" | "tablet" | "desktop";
  os: string;
  browser: string;
  screenSize: string;
  connectionType?: string;
}

// A/B Test configuration
export interface ABTestConfig {
  id: string;
  name: string;
  variants: ABVariant[];
  trafficSplit: number; // Percentage of users to include in test
  startDate: Date;
  endDate?: Date;
  isActive: boolean;
}

export interface ABVariant {
  id: string;
  name: string;
  weight: number; // Traffic distribution weight
  config: Record<string, any>;
}

// Performance metrics
export interface PerformanceMetrics {
  fcp?: number; // First Contentful Paint
  lcp?: number; // Largest Contentful Paint
  fid?: number; // First Input Delay
  cls?: number; // Cumulative Layout Shift
  ttfb?: number; // Time to First Byte
  tti?: number; // Time to Interactive
}

class AdvancedAnalytics {
  private userJourney: UserJourney | null = null;
  private abTests: Map<string, ABTestConfig> = new Map();
  private performanceMetrics: PerformanceMetrics | null = null;

  constructor() {
    this.initializeUserJourney();
    this.setupPerformanceMonitoring();
    this.loadABTests();
  }

  // User Journey Tracking
  private initializeUserJourney(): void {
    const sessionId = this.generateSessionId();
    const deviceInfo = this.getDeviceInfo();

    this.userJourney = {
      sessionId,
      startTime: new Date(),
      currentStep: "landing_page_view",
      steps: [],
      deviceInfo,
      userAgent: navigator.userAgent,
      referrer: document.referrer,
    };

    this.trackJourneyStep("landing_page_view", {
      page: window.location.pathname,
      referrer: document.referrer,
    });
  }

  private generateSessionId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  private getDeviceInfo(): DeviceInfo {
    const userAgent = navigator.userAgent;
    const screenWidth = window.screen.width;
    const screenHeight = window.screen.height;

    let type: "mobile" | "tablet" | "desktop" = "desktop";
    if (screenWidth < 768) type = "mobile";
    else if (screenWidth < 1024) type = "tablet";

    let os = "Unknown";
    if (userAgent.includes("Windows")) os = "Windows";
    else if (userAgent.includes("Mac")) os = "macOS";
    else if (userAgent.includes("Linux")) os = "Linux";
    else if (userAgent.includes("Android")) os = "Android";
    else if (userAgent.includes("iOS")) os = "iOS";

    let browser = "Unknown";
    if (userAgent.includes("Chrome")) browser = "Chrome";
    else if (userAgent.includes("Firefox")) browser = "Firefox";
    else if (userAgent.includes("Safari")) browser = "Safari";
    else if (userAgent.includes("Edge")) browser = "Edge";

    const connectionType =
      (navigator as any).connection?.effectiveType || "unknown";

    return {
      type,
      os,
      browser,
      screenSize: `${screenWidth}x${screenHeight}`,
      connectionType,
    };
  }

  public trackJourneyStep(
    step: string,
    properties?: Record<string, any>
  ): void {
    if (!this.userJourney) return;

    const now = new Date();
    const lastStep = this.userJourney.steps[this.userJourney.steps.length - 1];
    const duration = lastStep
      ? now.getTime() - lastStep.timestamp.getTime()
      : 0;

    const journeyStep: JourneyStep = {
      step,
      timestamp: now,
      duration,
      properties,
    };

    this.userJourney.steps.push(journeyStep);
    this.userJourney.currentStep = step;

    // Track with analytics
    analytics.trackEvent("journey_step", {
      session_id: this.userJourney.sessionId,
      step,
      duration,
      step_number: this.userJourney.steps.length,
      ...properties,
    });

    // Send to server for analysis (in production)
    this.sendJourneyData(journeyStep);
  }

  private sendJourneyData(step: JourneyStep): void {
    // In production, this would send to your analytics server
    if (process.env.NODE_ENV === "development") {
      // Journey step data for development
    }
  }

  // A/B Testing
  private loadABTests(): void {
    // Load A/B test configurations
    const tests: ABTestConfig[] = [
      {
        id: "hero_cta_test",
        name: "Hero CTA Button Test",
        variants: [
          {
            id: "control",
            name: "Control",
            weight: 50,
            config: { buttonText: "Try it Free" },
          },
          {
            id: "variant_a",
            name: "Variant A",
            weight: 25,
            config: { buttonText: "Start Your Journey" },
          },
          {
            id: "variant_b",
            name: "Variant B",
            weight: 25,
            config: { buttonText: "Get Started Now" },
          },
        ],
        trafficSplit: 100,
        startDate: new Date("2024-01-01"),
        isActive: true,
      },
      {
        id: "email_capture_test",
        name: "Email Capture Form Test",
        variants: [
          {
            id: "control",
            name: "Control",
            weight: 50,
            config: { formType: "inline" },
          },
          {
            id: "variant_a",
            name: "Variant A",
            weight: 50,
            config: { formType: "modal" },
          },
        ],
        trafficSplit: 50,
        startDate: new Date("2024-01-01"),
        isActive: true,
      },
    ];

    tests.forEach((test) => this.abTests.set(test.id, test));
  }

  public getABTestVariant(testId: string): string | null {
    const test = this.abTests.get(testId);
    if (!test || !test.isActive) return null;

    // Check if user should be included in test
    const userHash = this.hashString(this.userJourney?.sessionId || "");
    const userInTest = userHash % 100 < test.trafficSplit;

    if (!userInTest) return null;

    // Assign variant based on weights
    const random = Math.random() * 100;
    let cumulativeWeight = 0;

    for (const variant of test.variants) {
      cumulativeWeight += variant.weight;
      if (random <= cumulativeWeight) {
        return variant.id;
      }
    }

    return test.variants[0].id; // Fallback to first variant
  }

  public getABTestConfig(
    testId: string,
    variantId: string
  ): Record<string, any> | null {
    const test = this.abTests.get(testId);
    if (!test) return null;

    const variant = test.variants.find((v) => v.id === variantId);
    return variant?.config || null;
  }

  public trackABTestEvent(
    testId: string,
    variantId: string,
    event: string,
    properties?: Record<string, any>
  ): void {
    analytics.trackEvent("ab_test_event", {
      test_id: testId,
      variant_id: variantId,
      event,
      session_id: this.userJourney?.sessionId,
      ...properties,
    });
  }

  private hashString(str: string): number {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash);
  }

  // Performance Monitoring
  private setupPerformanceMonitoring(): void {
    if ("PerformanceObserver" in window) {
      this.observeWebVitals();
    }
  }

  private observeWebVitals(): void {
    // First Contentful Paint
    new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const fcp = entries[entries.length - 1];
      if (fcp) {
        this.performanceMetrics = {
          ...this.performanceMetrics,
          fcp: fcp.startTime,
        };
        this.trackPerformanceMetric("fcp", fcp.startTime);
      }
    }).observe({ entryTypes: ["paint"] });

    // Largest Contentful Paint
    new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const lcp = entries[entries.length - 1];
      if (lcp) {
        this.performanceMetrics = {
          ...this.performanceMetrics,
          lcp: lcp.startTime,
        };
        this.trackPerformanceMetric("lcp", lcp.startTime);
      }
    }).observe({ entryTypes: ["largest-contentful-paint"] });

    // First Input Delay
    new PerformanceObserver((list) => {
      const entries = list.getEntries();
      entries.forEach((entry) => {
        if (entry.entryType === "first-input") {
          const firstInputEntry = entry as PerformanceEventTiming;
          const fid =
            firstInputEntry.processingStart - firstInputEntry.startTime;
          this.performanceMetrics = { ...this.performanceMetrics, fid };
          this.trackPerformanceMetric("fid", fid);
        }
      });
    }).observe({ entryTypes: ["first-input"] });

    // Cumulative Layout Shift
    new PerformanceObserver((list) => {
      let cls = 0;
      list.getEntries().forEach((entry: any) => {
        if (!entry.hadRecentInput) {
          cls += entry.value;
        }
      });
      this.performanceMetrics = { ...this.performanceMetrics, cls };
      this.trackPerformanceMetric("cls", cls);
    }).observe({ entryTypes: ["layout-shift"] });
  }

  private trackPerformanceMetric(metric: string, value: number): void {
    analytics.trackEvent("performance_metric", {
      metric,
      value,
      session_id: this.userJourney?.sessionId,
      device_type: this.userJourney?.deviceInfo.type,
      connection_type: this.userJourney?.deviceInfo.connectionType,
    });
  }

  // Enhanced conversion tracking
  public trackConversion(
    conversionType: string,
    value?: number,
    properties?: Record<string, any>
  ): void {
    analytics.trackConversion(conversionType, {
      conversionType,
      properties: {
        session_id: this.userJourney?.sessionId,
        journey_steps: this.userJourney?.steps.length,
        time_on_site: this.getTimeOnSite(),
        device_type: this.userJourney?.deviceInfo.type,
        value,
        ...properties,
      },
    });
  }

  private getTimeOnSite(): number {
    if (!this.userJourney) return 0;
    return Date.now() - this.userJourney.startTime.getTime();
  }

  // User engagement tracking
  public trackEngagement(
    action: string,
    properties?: Record<string, any>
  ): void {
    analytics.trackEvent("user_engagement", {
      action,
      session_id: this.userJourney?.sessionId,
      current_step: this.userJourney?.currentStep,
      time_on_site: this.getTimeOnSite(),
      ...properties,
    });
  }

  // Scroll depth tracking
  public trackScrollDepth(): void {
    let maxScrollDepth = 0;

    const trackScroll = () => {
      const scrollTop = window.pageYOffset;
      const docHeight =
        document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercent = (scrollTop / docHeight) * 100;

      if (scrollPercent > maxScrollDepth) {
        maxScrollDepth = scrollPercent;

        // Track at 25%, 50%, 75%, 100%
        if (maxScrollDepth >= 25 && maxScrollDepth < 50) {
          this.trackEngagement("scroll_25_percent");
        } else if (maxScrollDepth >= 50 && maxScrollDepth < 75) {
          this.trackEngagement("scroll_50_percent");
        } else if (maxScrollDepth >= 75 && maxScrollDepth < 100) {
          this.trackEngagement("scroll_75_percent");
        } else if (maxScrollDepth >= 100) {
          this.trackEngagement("scroll_100_percent");
        }
      }
    };

    window.addEventListener("scroll", trackScroll);
  }

  // Session end tracking
  public trackSessionEnd(): void {
    if (!this.userJourney) return;

    const sessionDuration = this.getTimeOnSite();
    const stepsCompleted = this.userJourney.steps.length;

    analytics.trackEvent("session_end", {
      session_id: this.userJourney.sessionId,
      duration: sessionDuration,
      steps_completed: stepsCompleted,
      final_step: this.userJourney.currentStep,
      device_info: this.userJourney.deviceInfo,
    });
  }
}

// Export singleton instance
export const advancedAnalytics = new AdvancedAnalytics();

// Convenience functions
export const trackJourneyStep = (
  step: string,
  properties?: Record<string, any>
) => {
  advancedAnalytics.trackJourneyStep(step, properties);
};

export const getABTestVariant = (testId: string): string | null => {
  return advancedAnalytics.getABTestVariant(testId);
};

export const getABTestConfig = (
  testId: string,
  variantId: string
): Record<string, any> | null => {
  return advancedAnalytics.getABTestConfig(testId, variantId);
};

export const trackABTestEvent = (
  testId: string,
  variantId: string,
  event: string,
  properties?: Record<string, any>
) => {
  advancedAnalytics.trackABTestEvent(testId, variantId, event, properties);
};

export const trackEngagement = (
  action: string,
  properties?: Record<string, any>
) => {
  advancedAnalytics.trackEngagement(action, properties);
};

export const trackConversion = (
  conversionType: string,
  value?: number,
  properties?: Record<string, any>
) => {
  advancedAnalytics.trackConversion(conversionType, value, properties);
};
